// ════════════════════════════════════════════════════════════════════════════
// CandidateManagementScreen — recruiter's directory of every candidate in
// their pipeline. Reads /api/recruitment/pipeline which JOINs applications +
// users + jobs + companies so each row carries real candidate_name from
// users.full_name and the JOINed job title — no "Unknown" placeholders.
//
// Tapping a card opens ApplicantDetailsScreen. The contact and schedule
// shortcuts on the bottom of each card thread the same applicant object
// through to those screens so they don't have to re-fetch.
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, ScrollView,
  RefreshControl, StatusBar, TextInput, ActivityIndicator, Image, Alert,
  Dimensions,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width } = Dimensions.get('window');
const PRIMARY = '#0e4e96';
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// Inlined fetch — same pattern as VideoScreen.js. No external dependency on
// utils/apiClient because that import path has been unreliable. Diagnostic
// logs surface every request/response in the Metro console.
const apiFetch = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = await AsyncStorage.getItem('auth_token').catch(() => null);
  console.log(`[CandidateMgmt] → ${options.method || 'GET'} ${url}`);
  let res;
  try {
    res = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
  } catch (e) {
    console.warn(`[CandidateMgmt] ← NETWORK FAIL ${url}:`, e?.message);
    throw new Error(`Network: ${e?.message || 'unreachable'}`);
  }
  console.log(`[CandidateMgmt] ← ${res.status} ${url}`);
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
  }
  return res.json();
};

// Map a pipeline row → camelCase shape the UI reads. Defaults are sensible:
// missing name falls through users.full_name (server JOIN) so we don't keep
// rendering "Unknown" or "Applicant" placeholders.
const normalise = (row) => ({
  id: String(row.id),
  applicationId: row.id,
  name:    row.candidate_name || 'Applicant',
  email:   row.candidate_email || '',
  phone:   row.candidate_phone || '',
  avatar:  row.candidate_avatar || null,
  location: row.candidate_location || '',
  currentTitle: row.current_title || '',
  currentCompany: row.current_company || '',
  jobId:    row.job_id,
  jobTitle: row.job_title || '',
  stage:    (row.stage || 'new').toLowerCase(),
  status:   row.status || 'submitted',
  appliedAt: row.created_at || row.applied_date || null,
  matchScore: row.match_score ?? null,
  interviewsScheduled: Number(row.interviews_scheduled || 0),
  nextInterviewAt: row.next_interview_at || null,
  raw: row,
});

// Stage definitions — drive the tab bar + filter logic.
// Colours match the badges shown on each card.
const STAGES = [
  { key: 'all',         label: 'All',         color: '#6B7280' },
  { key: 'new',         label: 'New',         color: '#3B82F6' },
  { key: 'reviewed',    label: 'Reviewed',    color: '#06B6D4' },
  { key: 'screening',   label: 'Screening',   color: '#0EA5E9' },
  { key: 'shortlisted', label: 'Shortlisted', color: '#8B5CF6' },
  { key: 'interviewed', label: 'Interviewed', color: '#F59E0B' },
  { key: 'offered',     label: 'Offered',     color: '#10B981' },
  { key: 'hired',       label: 'Hired',       color: '#059669' },
  { key: 'rejected',    label: 'Rejected',    color: '#EF4444' },
];

export default function CandidateManagementScreen({ navigation, route }) {
  // The "defaultStage" prop lets us reuse this component for the Selection
  // variant — CandidateSelectionScreen just renders this with stage=shortlisted.
  const defaultStage = route?.params?.defaultStage || 'all';
  const screenTitle  = route?.params?.title || 'Candidate Management';

  const [isLoading, setIsLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [activeStage, setActiveStage] = useState(defaultStage);
  const [searchQuery, setSearchQuery] = useState('');

  // ── Load pipeline ──────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    try {
      const cid = parseInt(await AsyncStorage.getItem('company_id') || '0', 10);
      console.log(`[CandidateMgmt] company_id from storage: ${cid}`);
      if (!cid) {
        setCandidates([]);
        Alert.alert('No company', 'Please sign in again — company id missing.');
        return;
      }
      // Real pipeline endpoint — JOINed with users + jobs + company + interview counts
      const rows = await apiFetch(`/api/recruitment/pipeline?company_id=${cid}&limit=500`);
      const list = Array.isArray(rows) ? rows : (Array.isArray(rows?.data) ? rows.data : []);
      const normalised = list.map(normalise);
      setCandidates(normalised);
      console.log(`[CandidateMgmt] loaded ${normalised.length} candidates`);
      // Cache for offline browsing
      await AsyncStorage.setItem('candidates_cache', JSON.stringify(normalised)).catch(() => {});

      // Company profile for the header — non-fatal if it fails
      try {
        const prof = await apiFetch(`/api/company-profile/${cid}`);
        const p = prof?.data || prof;
        if (p && (p.company_name || p.name)) setCompanyProfile(p);
      } catch (_) {
        const cached = await AsyncStorage.getItem('company_profile').catch(() => null);
        if (cached) { try { setCompanyProfile(JSON.parse(cached)); } catch {} }
      }
    } catch (e) {
      console.warn('[CandidateMgmt] loadData:', e?.message);
      // Offline cache fallback only — no mock data
      try {
        const cached = await AsyncStorage.getItem('candidates_cache');
        if (cached) setCandidates(JSON.parse(cached));
        else setCandidates([]);
      } catch { setCandidates([]); }
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  const onRefresh = () => { setRefreshing(true); loadData(); };

  // ── Derived: counts per stage + filtered list ──────────────────────────
  const stageCounts = candidates.reduce((acc, c) => {
    acc.all = (acc.all || 0) + 1;
    acc[c.stage] = (acc[c.stage] || 0) + 1;
    return acc;
  }, {});

  const filtered = candidates.filter(c => {
    if (activeStage !== 'all' && c.stage !== activeStage) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      String(c.name ?? '').toLowerCase().includes(q) ||
      String(c.email ?? '').toLowerCase().includes(q) ||
      String(c.jobTitle ?? '').toLowerCase().includes(q) ||
      String(c.currentCompany ?? '').toLowerCase().includes(q)
    );
  });

  // ── Navigation ──────────────────────────────────────────────────────────
  const openDetails = (c) =>
    navigation.navigate('ApplicantDetailsScreen', {
      applicantId: c.id, applicant: c.raw,
    });
  const openContact = (c) =>
    navigation.navigate('ContactCandidateScreen', {
      applicantId: c.id, applicant: c.raw,
    });
  const openSchedule = (c) =>
    navigation.navigate('ScheduleInterviewScreen', {
      applicantId: c.id, applicant: c.raw,
    });
  const openOffer = (c) =>
    navigation.navigate('MakeOfferScreen', {
      applicantId: c.id, applicant: c.raw,
    });

  // ── Render: loading ─────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <SafeAreaView style={st.loading}>
        <StatusBar barStyle="dark-content" />
        <ActivityIndicator size="large" color={PRIMARY} />
        <Text style={st.loadingTxt}>Loading candidates...</Text>
      </SafeAreaView>
    );
  }

  // ── Render: main ────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={st.container}>
      <StatusBar barStyle="light-content" />

      {/* Header */}
      <LinearGradient colors={['#7C3AED', '#8B5CF6']} style={st.header}>
        <View style={st.headerRow}>
          <TouchableOpacity onPress={() => navigation?.goBack()} style={st.hBtn}>
            <MaterialCommunityIcons name="arrow-left" size={24} color="#FFF" />
          </TouchableOpacity>
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={st.hTitle}>{screenTitle}</Text>
            <Text style={st.hSub}>
              {filtered.length} of {candidates.length} candidate{candidates.length === 1 ? '' : 's'}
              {companyProfile?.company_name ? ` · ${companyProfile.company_name}` : ''}
            </Text>
          </View>
          <TouchableOpacity style={st.hBtn} onPress={onRefresh}>
            <MaterialCommunityIcons name="refresh" size={22} color="#FFF" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {/* Search */}
      <View style={st.searchWrap}>
        <View style={st.searchRow}>
          <MaterialCommunityIcons name="magnify" size={20} color="#9CA3AF" />
          <TextInput
            style={st.searchInput}
            placeholder="Search by name, email, job, company"
            placeholderTextColor="#9CA3AF"
            value={searchQuery}
            onChangeText={setSearchQuery}
            returnKeyType="search"
          />
          {searchQuery ? (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <MaterialCommunityIcons name="close-circle" size={18} color="#9CA3AF" />
            </TouchableOpacity>
          ) : null}
        </View>
      </View>

      {/* Stage tabs */}
      <View style={st.tabsWrap}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 12 }}>
          {STAGES.map(s => {
            const count = stageCounts[s.key] || 0;
            // Hide stages with zero candidates (except "all")
            if (s.key !== 'all' && count === 0) return null;
            const active = activeStage === s.key;
            return (
              <TouchableOpacity
                key={s.key}
                style={[st.tab, active && { backgroundColor: s.color + '15', borderColor: s.color }]}
                onPress={() => setActiveStage(s.key)}
              >
                <Text style={[st.tabLabel, active && { color: s.color, fontWeight: '700' }]}>{s.label}</Text>
                <View style={[st.tabBadge, active && { backgroundColor: s.color }]}>
                  <Text style={[st.tabBadgeTxt, active && { color: '#FFF' }]}>{count}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>

      {/* Candidate list */}
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingVertical: 12, paddingBottom: 32 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[PRIMARY]} />}
        showsVerticalScrollIndicator={false}
      >
        {filtered.length === 0 ? (
          <View style={st.empty}>
            <MaterialCommunityIcons name="account-search" size={64} color="#D1D5DB" />
            <Text style={st.emptyTitle}>
              {candidates.length === 0 ? 'No candidates yet' : 'No matches'}
            </Text>
            <Text style={st.emptySub}>
              {candidates.length === 0
                ? 'Post a job and applications will appear here.'
                : `No candidates match "${searchQuery}"${activeStage !== 'all' ? ` in ${activeStage}` : ''}.`}
            </Text>
          </View>
        ) : (
          filtered.map(c => {
            const stageInfo = STAGES.find(s => s.key === c.stage) || STAGES[0];
            const initials = (c.name || 'A')
              .split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
            return (
              <View key={c.id} style={st.card}>
                <TouchableOpacity style={st.cardBody} onPress={() => openDetails(c)} activeOpacity={0.7}>
                  <View style={[st.avatar, { backgroundColor: stageInfo.color + '20' }]}>
                    {c.avatar
                      ? <Image source={{ uri: c.avatar }} style={st.avatarImg} />
                      : <Text style={[st.avatarTxt, { color: stageInfo.color }]}>{initials}</Text>}
                  </View>
                  <View style={st.cardMain}>
                    <Text style={st.cardName} numberOfLines={1}>{c.name}</Text>
                    <Text style={st.cardJob} numberOfLines={1}>
                      {c.jobTitle || c.currentTitle || c.email || '—'}
                    </Text>
                    <View style={st.cardMetaRow}>
                      <View style={[st.stageBadge, { backgroundColor: stageInfo.color + '15' }]}>
                        <Text style={[st.stageBadgeTxt, { color: stageInfo.color }]}>{stageInfo.label}</Text>
                      </View>
                      {c.interviewsScheduled > 0 && (
                        <View style={st.metaChip}>
                          <MaterialCommunityIcons name="calendar-clock" size={11} color="#6B7280" />
                          <Text style={st.metaChipTxt}>{c.interviewsScheduled}</Text>
                        </View>
                      )}
                      {c.matchScore != null && (
                        <View style={st.metaChip}>
                          <MaterialCommunityIcons name="chart-line" size={11} color="#6B7280" />
                          <Text style={st.metaChipTxt}>{Math.round(c.matchScore)}%</Text>
                        </View>
                      )}
                    </View>
                  </View>
                  <MaterialCommunityIcons name="chevron-right" size={20} color="#D1D5DB" />
                </TouchableOpacity>

                {/* Quick-action footer */}
                <View style={st.actionsRow}>
                  <TouchableOpacity style={st.actionBtn} onPress={() => openContact(c)}>
                    <MaterialCommunityIcons name="message-text-outline" size={16} color={PRIMARY} />
                    <Text style={st.actionTxt}>Contact</Text>
                  </TouchableOpacity>
                  <View style={st.actionSep} />
                  <TouchableOpacity style={st.actionBtn} onPress={() => openSchedule(c)}>
                    <MaterialCommunityIcons name="calendar-plus" size={16} color={PRIMARY} />
                    <Text style={st.actionTxt}>Schedule</Text>
                  </TouchableOpacity>
                  <View style={st.actionSep} />
                  <TouchableOpacity style={st.actionBtn} onPress={() => openOffer(c)}>
                    <MaterialCommunityIcons name="file-sign" size={16} color={PRIMARY} />
                    <Text style={st.actionTxt}>Offer</Text>
                  </TouchableOpacity>
                </View>
              </View>
            );
          })
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const st = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F3F4F6' },
  loading:   { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#F3F4F6' },
  loadingTxt:{ fontSize: 15, color: '#6B7280', marginTop: 12 },

  header:    { paddingTop: 12, paddingBottom: 16, paddingHorizontal: 16 },
  headerRow: { flexDirection: 'row', alignItems: 'center' },
  hBtn:      { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.12)', justifyContent: 'center', alignItems: 'center' },
  hTitle:    { fontSize: 20, fontWeight: 'bold', color: '#FFF' },
  hSub:      { fontSize: 12, color: '#DDD6FE', marginTop: 2 },

  searchWrap:{ backgroundColor: '#FFF', paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  searchRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F3F4F6', borderRadius: 10, paddingHorizontal: 12 },
  searchInput:{ flex: 1, fontSize: 14, color: '#0F172A', paddingVertical: 10, paddingHorizontal: 8 },

  tabsWrap:  { backgroundColor: '#FFF', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  tab:       { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 6, marginRight: 8, borderRadius: 999, borderWidth: 1, borderColor: '#E5E7EB', backgroundColor: '#F9FAFB' },
  tabLabel:  { fontSize: 12, color: '#6B7280', fontWeight: '500' },
  tabBadge:  { marginLeft: 6, paddingHorizontal: 6, paddingVertical: 1, borderRadius: 10, backgroundColor: '#E5E7EB' },
  tabBadgeTxt:{ fontSize: 11, color: '#6B7280', fontWeight: '600' },

  card:      { backgroundColor: '#FFF', marginHorizontal: 16, marginBottom: 10, borderRadius: 12, borderWidth: 1, borderColor: '#E5E7EB', overflow: 'hidden' },
  cardBody:  { flexDirection: 'row', alignItems: 'center', padding: 14 },
  avatar:    { width: 48, height: 48, borderRadius: 24, alignItems: 'center', justifyContent: 'center', marginRight: 12 },
  avatarImg: { width: 48, height: 48, borderRadius: 24 },
  avatarTxt: { fontSize: 16, fontWeight: '700' },
  cardMain:  { flex: 1 },
  cardName:  { fontSize: 15, fontWeight: '600', color: '#0F172A' },
  cardJob:   { fontSize: 13, color: '#6B7280', marginTop: 2 },
  cardMetaRow:{ flexDirection: 'row', alignItems: 'center', marginTop: 6, flexWrap: 'wrap' },
  stageBadge:{ paddingHorizontal: 8, paddingVertical: 2, borderRadius: 6, marginRight: 6 },
  stageBadgeTxt:{ fontSize: 11, fontWeight: '600', textTransform: 'capitalize' },
  metaChip:  { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 6, backgroundColor: '#F3F4F6', marginRight: 4 },
  metaChipTxt:{ fontSize: 11, color: '#6B7280', marginLeft: 3, fontWeight: '500' },

  actionsRow:{ flexDirection: 'row', borderTopWidth: 1, borderTopColor: '#F3F4F6', backgroundColor: '#FAFAFA' },
  actionBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 10 },
  actionTxt: { fontSize: 12, color: PRIMARY, fontWeight: '600', marginLeft: 6 },
  actionSep: { width: 1, backgroundColor: '#F3F4F6' },

  empty:     { alignItems: 'center', padding: 48 },
  emptyTitle:{ fontSize: 16, color: '#374151', fontWeight: '600', marginTop: 16 },
  emptySub:  { fontSize: 13, color: '#9CA3AF', marginTop: 6, textAlign: 'center', paddingHorizontal: 24 },
});
